import useSWR from 'swr';
import { useState, useEffect } from 'react';
import type { ProductDataType } from '@am3nd-org/pointcraft-types';

import { fetcher, apiEndpoints } from 'src/utils/axios';
import {
  InventoryLocalDB,
  getInventorySyncMeta,
  saveInventorySyncMeta,
  saveInventoryToLocalDb,
  INVENTORY_CACHE_SCHEMA_VERSION,
} from 'src/utils/Services/DB_Utils';

import { useDeviceSyncStore } from 'src/store/DeviceSync';

export function useInventoryData(enabled = true) {
  const [localProductsLoaded, setLocalProductsLoaded] = useState(false);
  const [localCheckDone, setLocalCheckDone] = useState(false);

  // Step 1: Run one-time chunk migration (idempotent), then check if we have local data.
  useEffect(() => {
    if (!enabled) {
      setLocalCheckDone(true);
      return;
    }

    const initLocal = async () => {
      try {
        // One-time migration from old chunked storage — no-op on subsequent boots.
        await InventoryLocalDB.migrateFromChunks();

        // Check whether the inventory table is populated.
        const [total, metadata] = await Promise.all([
          InventoryLocalDB.count(),
          getInventorySyncMeta(),
        ]);
        const businessId = useDeviceSyncStore.getState().businessData?.id;
        const ownsCurrentCache = Boolean(
          businessId &&
            metadata?.businessId === businessId &&
            metadata.schemaVersion === INVENTORY_CACHE_SCHEMA_VERSION &&
            metadata.recordCount === total
        );
        if (total > 0 && ownsCurrentCache) {
          setLocalProductsLoaded(true);
          console.log(`[useInventoryData] Local inventory ready: ${total} products`);
        } else if (total > 0) {
          await InventoryLocalDB.clearAll();
        }
      } catch (e) {
        console.error('[useInventoryData] Failed to initialise local inventory:', e);
      } finally {
        setLocalCheckDone(true);
      }
    };
    initLocal();
  }, [enabled]);

  // Step 2: Only hit the server when the local check found nothing.
  const { data: serverProducts } = useSWR<ProductDataType[]>(
    enabled && localCheckDone && !localProductsLoaded ? apiEndpoints.products.base : null,
    fetcher,
    { revalidateIfStale: false, revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  // Step 3: Persist server data to local DB when it arrives (no Zustand population).
  useEffect(() => {
    if (!enabled) return;
    if (!serverProducts || serverProducts.length === 0) return;
    // Write to both the new inventory table and the legacy chunks so the migration
    // sentinel still functions correctly on the next boot.
    const businessId = useDeviceSyncStore.getState().businessData?.id;
    InventoryLocalDB.saveAll(serverProducts)
      .then(async () => {
        if (!businessId) return;
        await saveInventorySyncMeta({
          version: 0,
          lastSyncedAt: new Date().toISOString(),
          businessId,
          schemaVersion: INVENTORY_CACHE_SCHEMA_VERSION,
          recordCount: serverProducts.length,
        });
      })
      .catch((e) => console.error('[useInventoryData] Failed to save inventory to table:', e));
    saveInventoryToLocalDb(serverProducts).catch((e) =>
      console.error('[useInventoryData] Failed to save inventory chunks:', e)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, serverProducts]);
}
